import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../_model/api-response';
import {
  DepositRiskOperationalSummaryDto,
  DepositRiskReviewActionRequest,
  DepositRiskReviewDetailsDto,
  DepositRiskReviewListItemDto,
  DepositRiskReviewQueryDto,
  LiquidityForecastCaptureResultDto,
  LiquidityIntelligenceAlertDto,
  LiquidityIntelligenceSummaryDto,
  LoginRiskDeploymentHealthDto,
  LoginRiskDeploymentStatusDto,
  MlDeploymentHealthAlertDto,
  MlLifecycleOverviewDto,
  MlOperationalAlertDto,
  MlPredictionDetailsDto,
  MlPredictionListItemDto,
  MlPredictionQueryDto,
  ResolveDepositRiskReviewRequest,
  WithdrawalRiskOperationalSummaryDto,
  MlTrainingReadinessDto,
  MlTrainingDataQueryDto,
  RunLoginRiskTrainingRequest,
  MlTrainingRunResultDto,
  MlRetrainingStatusDto,
  MlRetrainingCandidateDto,
  MlRetrainingEvaluationDto,
  MlCandidateGovernanceDto,
  MlCandidateGovernanceSummaryDto,
  MlCandidateShadowSelectionDto,
  MlCandidateShadowEvaluationStatusDto,
  MlCandidateShadowEvaluationResultDto,
  MlNetShadowComparisonStatusDto,
  MlNetShadowComparisonSummaryDto,
  MlNetDeploymentReadinessDto,
  MlModelDeploymentDto,
  PromoteLoginRiskModelRequest,
  UpdateLoginRiskCanaryRequest,
  MlAutomaticRollbackStatusDto,
  MlAutomaticRollbackEvaluationDto,
  OrderMatchIntelligenceSummaryDto,
  SettlementReliabilitySummaryDto,
  GatewayReliabilitySummaryDto,
  OperationalReliabilityAlertDto,
  MlEvaluationQueryDto,
  MlEvaluationSummaryDto
} from '../_model/machine-learning';
import { WithdrawalAdminListItemDto } from '../_model/withdrawal';

@Injectable({ providedIn: 'root' })
export class MachineLearningAdminService {
  private readonly baseUrl = `${environment.apiBaseUrl}/admin/machine-learning`;
  private readonly adminWithdrawalsUrl = `${environment.apiBaseUrl}/AdminWithdrawals`;

  constructor(private http: HttpClient) {}

  getLoginRiskLifecycleOverview(): Observable<ApiResponse<MlLifecycleOverviewDto>> {
    return this.http.get<ApiResponse<MlLifecycleOverviewDto>>(
      `${this.baseUrl}/lifecycle/login-risk/overview`
    );
  }

  getLoginRiskTrainingReadiness(query: MlTrainingDataQueryDto = {}): Observable<ApiResponse<MlTrainingReadinessDto>> {
    let params = new HttpParams();
    if (query.fromDate) params = params.set('FromDate', query.fromDate);
    if (query.toDate) params = params.set('ToDate', query.toDate);
    return this.http.get<ApiResponse<MlTrainingReadinessDto>>(`${this.baseUrl}/training/login-risk/readiness`, { params });
  }

  exportLoginRiskTrainingDataset(query: MlTrainingDataQueryDto = {}): Observable<HttpResponse<Blob>> {
    return this.http.post(`${this.baseUrl}/training/login-risk/export`, query, {
      observe: 'response',
      responseType: 'blob'
    });
  }

  trainLoginRiskModel(request: RunLoginRiskTrainingRequest): Observable<ApiResponse<MlTrainingRunResultDto>> {
    return this.http.post<ApiResponse<MlTrainingRunResultDto>>(`${this.baseUrl}/training/login-risk/run`, request);
  }

  getLoginRiskRetrainingStatus(): Observable<ApiResponse<MlRetrainingStatusDto>> {
    return this.http.get<ApiResponse<MlRetrainingStatusDto>>(`${this.baseUrl}/training/login-risk/retraining/status`);
  }

  getLoginRiskRetrainingHistory(take = 20): Observable<ApiResponse<MlRetrainingCandidateDto[]>> {
    return this.http.get<ApiResponse<MlRetrainingCandidateDto[]>>(`${this.baseUrl}/training/login-risk/retraining/history`, {
      params: new HttpParams().set('take', take)
    });
  }

  evaluateLoginRiskRetraining(force = false): Observable<ApiResponse<MlRetrainingEvaluationDto>> {
    return this.http.post<ApiResponse<MlRetrainingEvaluationDto>>(`${this.baseUrl}/training/login-risk/retraining/evaluate`, { force });
  }

  getLoginRiskCandidates(status?: string | null, take = 50): Observable<ApiResponse<MlCandidateGovernanceDto[]>> {
    let params = new HttpParams().set('take', take);
    if (status) params = params.set('status', status);
    return this.http.get<ApiResponse<MlCandidateGovernanceDto[]>>(`${this.baseUrl}/governance/login-risk/candidates`, { params });
  }

  getLoginRiskCandidateSummary(): Observable<ApiResponse<MlCandidateGovernanceSummaryDto>> {
    return this.http.get<ApiResponse<MlCandidateGovernanceSummaryDto>>(`${this.baseUrl}/governance/login-risk/summary`);
  }

  getLoginRiskCandidate(id: string): Observable<ApiResponse<MlCandidateGovernanceDto>> {
    return this.http.get<ApiResponse<MlCandidateGovernanceDto>>(`${this.baseUrl}/governance/login-risk/candidates/${id}`);
  }

  approveLoginRiskCandidate(id: string, notes?: string | null): Observable<ApiResponse<MlCandidateGovernanceDto>> {
    return this.http.post<ApiResponse<MlCandidateGovernanceDto>>(`${this.baseUrl}/governance/login-risk/candidates/${id}/approve-shadow`, { notes: notes || null });
  }

  rejectLoginRiskCandidate(id: string, reason: string): Observable<ApiResponse<MlCandidateGovernanceDto>> {
    return this.http.post<ApiResponse<MlCandidateGovernanceDto>>(`${this.baseUrl}/governance/login-risk/candidates/${id}/reject`, { reason });
  }

  selectLoginRiskCandidateForShadow(id: string, notes?: string | null): Observable<ApiResponse<MlCandidateShadowSelectionDto>> {
    return this.http.post<ApiResponse<MlCandidateShadowSelectionDto>>(`${this.baseUrl}/governance/login-risk/candidates/${id}/select-shadow`, { notes: notes || null });
  }

  getLoginRiskShadowSelection(): Observable<ApiResponse<MlCandidateShadowSelectionDto | null>> {
    return this.http.get<ApiResponse<MlCandidateShadowSelectionDto | null>>(`${this.baseUrl}/governance/login-risk/shadow-selection`);
  }

  stopLoginRiskShadowSelection(reason: string): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/governance/login-risk/shadow-selection/stop`, { reason });
  }

  getLoginRiskCandidateShadowEvaluationStatus(): Observable<ApiResponse<MlCandidateShadowEvaluationStatusDto>> {
    return this.http.get<ApiResponse<MlCandidateShadowEvaluationStatusDto>>(`${this.baseUrl}/governance/login-risk/shadow-evaluation/status`);
  }

  evaluateLoginRiskCandidateShadow(completeIfReady: boolean, notes?: string | null): Observable<ApiResponse<MlCandidateShadowEvaluationResultDto>> {
    return this.http.post<ApiResponse<MlCandidateShadowEvaluationResultDto>>(`${this.baseUrl}/governance/login-risk/shadow-evaluation/evaluate`, {
      completeIfReady,
      notes: notes || null
    });
  }

  getLoginRiskShadowComparisonStatus(): Observable<ApiResponse<MlNetShadowComparisonStatusDto>> {
    return this.http.get<ApiResponse<MlNetShadowComparisonStatusDto>>(`${this.baseUrl}/shadow-comparison/login-risk/status`);
  }

  getLoginRiskShadowComparisonSummary(modelVersion?: string | null): Observable<ApiResponse<MlNetShadowComparisonSummaryDto>> {
    let params = new HttpParams();
    if (modelVersion) params = params.set('ModelVersion', modelVersion);
    return this.http.get<ApiResponse<MlNetShadowComparisonSummaryDto>>(`${this.baseUrl}/shadow-comparison/login-risk/summary`, { params });
  }

  getLoginRiskDeploymentReadiness(modelVersion: string): Observable<ApiResponse<MlNetDeploymentReadinessDto>> {
    return this.http.get<ApiResponse<MlNetDeploymentReadinessDto>>(`${this.baseUrl}/deployment/login-risk/readiness`, {
      params: new HttpParams().set('modelVersion', modelVersion)
    });
  }

  getLoginRiskDeploymentHistory(): Observable<ApiResponse<MlModelDeploymentDto[]>> {
    return this.http.get<ApiResponse<MlModelDeploymentDto[]>>(`${this.baseUrl}/deployment/login-risk/history`);
  }

  promoteLoginRiskModel(request: PromoteLoginRiskModelRequest): Observable<ApiResponse<MlModelDeploymentDto>> {
    return this.http.post<ApiResponse<MlModelDeploymentDto>>(`${this.baseUrl}/deployment/login-risk/promote`, request);
  }

  updateLoginRiskCanary(deploymentId: string, request: UpdateLoginRiskCanaryRequest): Observable<ApiResponse<MlModelDeploymentDto>> {
    return this.http.put<ApiResponse<MlModelDeploymentDto>>(`${this.baseUrl}/deployment/login-risk/${deploymentId}/canary`, request);
  }

  rollbackLoginRiskModel(reason: string): Observable<ApiResponse<LoginRiskDeploymentStatusDto>> {
    return this.http.post<ApiResponse<LoginRiskDeploymentStatusDto>>(`${this.baseUrl}/deployment/login-risk/rollback`, { reason });
  }

  getLoginRiskAutomaticRollbackStatus(): Observable<ApiResponse<MlAutomaticRollbackStatusDto>> {
    return this.http.get<ApiResponse<MlAutomaticRollbackStatusDto>>(`${this.baseUrl}/deployment/login-risk/automatic-rollback/status`);
  }

  evaluateLoginRiskAutomaticRollback(): Observable<ApiResponse<MlAutomaticRollbackEvaluationDto>> {
    return this.http.post<ApiResponse<MlAutomaticRollbackEvaluationDto>>(`${this.baseUrl}/deployment/login-risk/automatic-rollback/evaluate`, {});
  }

  getPredictions(query: MlPredictionQueryDto): Observable<ApiResponse<MlPredictionListItemDto[]>> {
    let params = new HttpParams();
    if (query.predictionType) params = params.set('PredictionType', query.predictionType);
    if (query.entityType) params = params.set('EntityType', query.entityType);
    if (query.classification) params = params.set('Classification', query.classification);
    if (query.provider) params = params.set('Provider', query.provider);
    if (query.actualOutcome) params = params.set('ActualOutcome', query.actualOutcome);
    if (query.hasOutcome != null) params = params.set('HasOutcome', query.hasOutcome);
    if (query.isShadowMode != null) params = params.set('IsShadowMode', query.isShadowMode);
    params = params.set('Page', query.page ?? 1);
    params = params.set('RowsPerPage', query.rowsPerPage ?? 20);

    return this.http.get<ApiResponse<MlPredictionListItemDto[]>>(
      `${this.baseUrl}/predictions`,
      { params }
    );
  }

  getPrediction(id: string): Observable<ApiResponse<MlPredictionDetailsDto>> {
    return this.http.get<ApiResponse<MlPredictionDetailsDto>>(`${this.baseUrl}/predictions/${id}`);
  }

  getWithdrawalRiskSummary(): Observable<ApiResponse<WithdrawalRiskOperationalSummaryDto>> {
    return this.http.get<ApiResponse<WithdrawalRiskOperationalSummaryDto>>(`${this.baseUrl}/withdrawal-risk/summary`);
  }

  getWithdrawalRiskAlerts(): Observable<ApiResponse<MlOperationalAlertDto[]>> {
    return this.http.get<ApiResponse<MlOperationalAlertDto[]>>(`${this.baseUrl}/withdrawal-risk/alerts`);
  }

  getWithdrawalReviewsByStatus(status: string, size = 50): Observable<ApiResponse<WithdrawalAdminListItemDto[]>> {
    const params = new HttpParams()
      .set('riskReviewStatus', status)
      .set('page', 1)
      .set('size', size);

    return this.http.get<ApiResponse<WithdrawalAdminListItemDto[]>>(this.adminWithdrawalsUrl, { params });
  }


  approveWithdrawalRiskReview(id: string, notes: string): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.adminWithdrawalsUrl}/${id}/risk-review/approve`, { notes });
  }

  rejectWithdrawalRiskReview(id: string, notes: string): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.adminWithdrawalsUrl}/${id}/risk-review/reject`, { notes });
  }

  reopenWithdrawalRiskReview(id: string, notes: string): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.adminWithdrawalsUrl}/${id}/risk-review/reconciliation/reopen`, { notes });
  }

  markWithdrawalSubmittedAfterReconciliation(
    id: string,
    payload: { notes: string; gatewayReference: string; providerTransactionId?: string | null }
  ): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(
      `${this.adminWithdrawalsUrl}/${id}/risk-review/reconciliation/mark-submitted`,
      payload
    );
  }

  getDepositRiskSummary(): Observable<ApiResponse<DepositRiskOperationalSummaryDto>> {
    return this.http.get<ApiResponse<DepositRiskOperationalSummaryDto>>(`${this.baseUrl}/deposit-risk/summary`);
  }

  getDepositRiskAlerts(): Observable<ApiResponse<MlOperationalAlertDto[]>> {
    return this.http.get<ApiResponse<MlOperationalAlertDto[]>>(`${this.baseUrl}/deposit-risk/alerts`);
  }

  getDepositRiskReviews(query: DepositRiskReviewQueryDto): Observable<ApiResponse<DepositRiskReviewListItemDto[]>> {
    let params = new HttpParams();
    if (query.status) params = params.set('Status', query.status);
    if (query.classification) params = params.set('Classification', query.classification);
    if (query.sourceOfFundsRecommended != null) {
      params = params.set('SourceOfFundsRecommended', query.sourceOfFundsRecommended);
    }
    params = params.set('Page', query.page ?? 1);
    params = params.set('RowsPerPage', query.rowsPerPage ?? 20);

    return this.http.get<ApiResponse<DepositRiskReviewListItemDto[]>>(
      `${this.baseUrl}/deposit-risk/reviews`,
      { params }
    );
  }

  getDepositRiskReview(id: string): Observable<ApiResponse<DepositRiskReviewDetailsDto>> {
    return this.http.get<ApiResponse<DepositRiskReviewDetailsDto>>(`${this.baseUrl}/deposit-risk/reviews/${id}`);
  }

  getDepositRiskResolutions(): Observable<ApiResponse<string[]>> {
    return this.http.get<ApiResponse<string[]>>(`${this.baseUrl}/deposit-risk/resolutions`);
  }

  startDepositRiskReview(id: string, payload: DepositRiskReviewActionRequest): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/deposit-risk/reviews/${id}/start`, payload);
  }

  requestDepositSourceOfFunds(id: string, payload: DepositRiskReviewActionRequest): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/deposit-risk/reviews/${id}/source-of-funds`, payload);
  }

  escalateDepositRiskReview(id: string, payload: DepositRiskReviewActionRequest): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/deposit-risk/reviews/${id}/escalate`, payload);
  }

  resolveDepositRiskReview(id: string, payload: ResolveDepositRiskReviewRequest): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/deposit-risk/reviews/${id}/resolve`, payload);
  }

  getLiquiditySummary(horizonHours?: number): Observable<ApiResponse<LiquidityIntelligenceSummaryDto>> {
    let params = new HttpParams();
    if (horizonHours != null) params = params.set('horizonHours', horizonHours);
    return this.http.get<ApiResponse<LiquidityIntelligenceSummaryDto>>(`${this.baseUrl}/liquidity/summary`, { params });
  }

  getLiquidityAlerts(): Observable<ApiResponse<LiquidityIntelligenceAlertDto[]>> {
    return this.http.get<ApiResponse<LiquidityIntelligenceAlertDto[]>>(`${this.baseUrl}/liquidity/alerts`);
  }

  captureLiquidityForecasts(): Observable<ApiResponse<LiquidityForecastCaptureResultDto>> {
    return this.http.post<ApiResponse<LiquidityForecastCaptureResultDto>>(`${this.baseUrl}/liquidity/forecasts/capture`, {});
  }

  getLoginRiskDeploymentStatus(): Observable<ApiResponse<LoginRiskDeploymentStatusDto>> {
    return this.http.get<ApiResponse<LoginRiskDeploymentStatusDto>>(`${this.baseUrl}/deployment/login-risk/status`);
  }

  getLoginRiskDeploymentHealth(windowHours = 24): Observable<ApiResponse<LoginRiskDeploymentHealthDto>> {
    const params = new HttpParams().set('WindowHours', windowHours);
    return this.http.get<ApiResponse<LoginRiskDeploymentHealthDto>>(`${this.baseUrl}/deployment/login-risk/health`, { params });
  }

  getLoginRiskDeploymentAlerts(windowHours = 24): Observable<ApiResponse<MlDeploymentHealthAlertDto[]>> {
    const params = new HttpParams().set('WindowHours', windowHours);
    return this.http.get<ApiResponse<MlDeploymentHealthAlertDto[]>>(`${this.baseUrl}/deployment/login-risk/alerts`, { params });
  }

  getMatchingIntelligenceSummary(fromDate?: string | null, toDate?: string | null): Observable<ApiResponse<OrderMatchIntelligenceSummaryDto>> {
    let params = new HttpParams();
    if (fromDate) params = params.set('FromDate', fromDate);
    if (toDate) params = params.set('ToDate', toDate);
    return this.http.get<ApiResponse<OrderMatchIntelligenceSummaryDto>>(`${this.baseUrl}/matching/summary`, { params });
  }

  getSettlementReliabilitySummary(fromDate?: string | null, toDate?: string | null): Observable<ApiResponse<SettlementReliabilitySummaryDto>> {
    let params = new HttpParams();
    if (fromDate) params = params.set('FromDate', fromDate);
    if (toDate) params = params.set('ToDate', toDate);
    return this.http.get<ApiResponse<SettlementReliabilitySummaryDto>>(`${this.baseUrl}/operational-reliability/settlements/summary`, { params });
  }

  getGatewayReliabilitySummary(fromDate?: string | null, toDate?: string | null): Observable<ApiResponse<GatewayReliabilitySummaryDto>> {
    let params = new HttpParams();
    if (fromDate) params = params.set('FromDate', fromDate);
    if (toDate) params = params.set('ToDate', toDate);
    return this.http.get<ApiResponse<GatewayReliabilitySummaryDto>>(`${this.baseUrl}/operational-reliability/gateways/summary`, { params });
  }

  getOperationalReliabilityAlerts(): Observable<ApiResponse<OperationalReliabilityAlertDto[]>> {
    return this.http.get<ApiResponse<OperationalReliabilityAlertDto[]>>(`${this.baseUrl}/operational-reliability/alerts`);
  }

  getEvaluationSummary(query: MlEvaluationQueryDto = {}): Observable<ApiResponse<MlEvaluationSummaryDto>> {
    let params = new HttpParams();
    if (query.predictionType) params = params.set('PredictionType', query.predictionType);
    if (query.provider) params = params.set('Provider', query.provider);
    if (query.modelCode) params = params.set('ModelCode', query.modelCode);
    if (query.modelVersion) params = params.set('ModelVersion', query.modelVersion);
    if (query.fromDate) params = params.set('FromDate', query.fromDate);
    if (query.toDate) params = params.set('ToDate', query.toDate);
    return this.http.get<ApiResponse<MlEvaluationSummaryDto>>(`${this.baseUrl}/evaluation/summary`, { params });
  }

}
